import { Injectable } from '@angular/core'
import { merge, Observable, of } from 'rxjs'
import { filter, first, map, switchMap } from 'rxjs/operators'
import { cloneDeep } from 'lodash-es'
import { Dayjs } from 'dayjs'

import { Vehicle, VehicleRoute, VehicleStatus } from '@shared/models/vehicle.model'
import { VehiclesService } from '@app/modules/client/vehicles/vehicles.service'
import { MapApiService } from '../../map-api.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { RoleType } from '@core/types/role.type'
import { User } from '@shared/models/user.model'
import { GeofencesFilterValues, VehicleFilterValues } from '../../shared/models/map-settings.model'
import { RoutesHistory } from '@shared/models/route.model'
import { sameId } from '@core/helpers/same-id'

@Injectable()
export class MapVehiclesService {
  private readonly defaultFields = ['status']

  private readonly fields: string[] = [
    'defaultLabel',
    'depot',
    'driver',
    'groups',
    'device.id',
    'device.status',
    'device.trackerData',
    'areas',
    'model',
    'regNo',
    'type',
    'typeId',
  ]

  constructor(
    private vehiclesService: VehiclesService,
    private mapApiService: MapApiService,
    private authService: AuthService,
  ) {}

  getVehicles(filterValues: VehicleFilterValues | GeofencesFilterValues = null, fields = this.fields): Observable<Vehicle[]> {
    const isAvailable = ({ status }: Vehicle) => status === VehicleStatus.Online || status === VehicleStatus.Offline
    const addStatusList = (vehicle: Vehicle): Vehicle => {
      const cloneVehicle = cloneDeep(vehicle)
      if (cloneVehicle?.device?.status) {
        cloneVehicle.device.statusList = cloneVehicle?.device?.status
      }
      return cloneVehicle
    }

    const allFields = [...fields, ...this.defaultFields]

    return this.vehiclesService.getVehicles(0, 2000, null, filterValues, allFields, true).pipe(
      map((vehicles: { data: Vehicle[] }) => vehicles.data),
      map((vehicles: Vehicle[]) => vehicles.filter(isAvailable)),
      map((vehicles: Vehicle[]) => vehicles.map(addStatusList)),
      switchMap((vehicles: Vehicle[]) => this.transformedVehicles(vehicles)),
    )
  }

  private transformedVehicles(vehicles: Vehicle[]): Observable<Vehicle[]> {
    const currentUser$: Observable<User> = this.authService.currentUser$.pipe(first())

    const isDriver$: Observable<Vehicle[]> = currentUser$.pipe(
      filter((user) => user.role.name === RoleType.Driver),
      switchMap((user: User) => {
        const isDriver = ({ driver }: Vehicle) => driver
        const toCurrentVehicle = ({ driver }: Vehicle) => sameId(user.id, driver.id)
        const currentVehicle: Vehicle = vehicles.filter(isDriver).find(toCurrentVehicle)
        return currentVehicle ? of([currentVehicle]) : of(null)
      }),
    )

    const isNotDriver$: Observable<Vehicle[]> = currentUser$.pipe(
      filter((user) => user.role.name !== RoleType.Driver),
      switchMap(() => of(vehicles)),
    )

    return merge(isDriver$, isNotDriver$)
  }

  getVehiclesRoutes(id: number | string, dateFrom: Dayjs, dateTo: Dayjs): Observable<VehicleRoute[]> {
    return this.mapApiService.getRouteList(`/vehicles/${id}/routes`, dateFrom, dateTo)
  }

  getVehiclesAssetsRoutes(id: number | string, dateFrom: Dayjs, dateTo: Dayjs): Observable<VehicleRoute[]> {
    return this.mapApiService.getRouteList(`/assets/${id}/routes`, dateFrom, dateTo)
  }

  getRoutesHistory(id: number | string, dateFrom: Dayjs, dateTo: Dayjs): Observable<RoutesHistory> {
    return this.mapApiService.getRoutesHistory(`/vehicles/${id}/routes`, dateFrom, dateTo)
  }
}
